import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Button, SortableList } from '@southfields-digital/mpxlive-components';

import DisplayHubStory, { IDisplayHubStoryProps } from 'src/components/DisplayHubStory';

import { StateType } from 'src/redux/reducers';
import { HandleMediaOptions } from '../DisplayHubStory/types';
import {
  REQUEST_DELETE_PLAYLIST_DISPLAY_HUB_STORY,
  REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB_STORY,
  REQUEST_REORDER_PLAYLIST_DISPLAY_HUB_STORY,
  REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB_STORY,
} from 'src/redux/reducers/playlistDisplayHubStory';

import styles from './DisplayHubStories.module.scss';

type Props = {
  selectedPlaylistDisplayHub: PlaylistDisplayHub;
  handleCreateStory: () => void;
  handleMedia: (properties: ImageProperties, options: HandleMediaOptions) => void;
};

export default function DisplayHubStories({
  selectedPlaylistDisplayHub,
  handleMedia,
  handleCreateStory,
}: Props) {
  const dispatch = useDispatch();

  const { playlistDisplayHubStories, selectedPlaylistDisplayHubStory } = useSelector(
    (state: StateType) => state.playlistDisplayHubStory
  );

  const handleSortStories = (sortedValues: IDisplayHubStoryProps[]) => {
    const sortedStories: string[] = sortedValues.map((item) => item.id);
    dispatch({
      type: REQUEST_REORDER_PLAYLIST_DISPLAY_HUB_STORY,
      payload: {
        playlistDisplayHubId: selectedPlaylistDisplayHub.id,
        playlistDisplayHubStoriesIds: sortedStories,
      },
    });
  };

  const handleDeleteStory = (story: PlaylistDisplayHubStory) =>
    dispatch({
      type: REQUEST_DELETE_PLAYLIST_DISPLAY_HUB_STORY,
      payload: {
        playlistDisplayHubId: selectedPlaylistDisplayHub.id,
        playlistDisplayHubStoryId: story.id,
      },
    });

  const handleDuplicateStory = (story: PlaylistDisplayHubStory) =>
    dispatch({
      type: REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB_STORY,
      payload: {
        playlistDisplayHubId: selectedPlaylistDisplayHub.id,
        playlistDisplayHubStoryId: story.id,
      },
    });

  const handleClickStory = (playlistDisplayHubStory: PlaylistDisplayHubStory) =>
    dispatch({
      type: REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB_STORY,
      payload: { playlistDisplayHubStory },
    });

  const items: IDisplayHubStoryProps[] = useMemo(() => {
    if (!playlistDisplayHubStories) return [];

    return playlistDisplayHubStories.map((story) => ({
      id: story.id,
      name: story.name,
      playlistGraphicIds: story.playlistGraphicIds,
      active: selectedPlaylistDisplayHubStory?.id === story.id,
      variant: 'default',
      onClick: () => handleClickStory(story),
      onDuplicate: () => handleDuplicateStory(story),
      onDelete: () => handleDeleteStory(story),
      onHandleMedia: handleMedia,
    }));
  }, [playlistDisplayHubStories, selectedPlaylistDisplayHubStory]);

  return (
    <div className={styles.Container}>
      <SortableList
        activeId={selectedPlaylistDisplayHubStory?.id}
        items={items}
        setItems={handleSortStories}
        Component={DisplayHubStory}
      />
      <div className={styles.CreateContainer}>
        <Button
          variant="tertiary"
          iconProps={{ icon: 'plus', color: '#ffffff', weight: 'bold' }}
          onClick={handleCreateStory}
        />
      </div>
    </div>
  );
}
